import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Search, ArrowRightLeft, Loader2, AlertCircle, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export const InternalTransfers = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/internal-transfers`, getAuthHeaders());
      setItems(res.data || []);
    } catch { setError('Failed to fetch internal transfers'); }
    finally { setLoading(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const filtered = items.filter(i =>
    (i.transfer_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (i.from_company_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (i.to_company_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Internal Transfers</h1>
          <p className="text-slate-600 mt-1">Stock transfers between companies</p>
        </div>
        <button onClick={() => navigate('/admin/inventory/internal-transfers/new')}
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
          <Plus className="h-5 w-5" /> Create Internal Transfer
        </button>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by transfer number or company..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <ArrowRightLeft className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No internal transfers yet</h3>
            <p className="text-slate-500 mb-6">Create your first internal transfer to move stock between companies.</p>
            <button onClick={() => navigate('/admin/inventory/internal-transfers/new')}
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600">
              <Plus className="h-5 w-5" /> Create First Transfer
            </button>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-7 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Transfer No.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">From Company</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-1">→</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">To Company</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Total</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map(item => (
              <div key={item.id} className="grid grid-cols-1 md:grid-cols-7 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="text-slate-600 text-sm">{formatDate(item.transfer_date)}</div>
                <div className="font-mono font-medium text-slate-900">{item.transfer_number}</div>
                <div className="text-slate-700 text-sm">{item.from_company_name || '-'}</div>
                <div className="text-center"><ArrowRightLeft className="h-4 w-4 text-orange-500 mx-auto" /></div>
                <div className="text-slate-700 text-sm">{item.to_company_name || '-'}</div>
                <div className="font-medium text-slate-900">₹{(item.total || 0).toLocaleString()}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => navigate(`/admin/inventory/internal-transfers/${item.id}`)}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors">
                    <Eye className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
